import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import { tileUrl, OSM_ATTRIBUTION } from "../utils/map_tiles"

// Network map: one bubble per city (contacts share their city centroid),
// sized by contact count. Tapping a bubble opens a panel listing the people
// there. Free OSM raster tiles; all rendering is client-side from one
// /network/map_data payload.
export default class extends Controller {
    static targets = ["map", "panel", "panelTitle", "panelList", "footer"]
    static values = { url: String }

    async connect() {
        if (!this.hasMapTarget) return

        this._map = L.map(this.mapTarget, { zoomControl: true, worldCopyJump: true })
        L.tileLayer(tileUrl(), {
            maxZoom: 18,
            attribution: OSM_ATTRIBUTION
        }).addTo(this._map)
        this._map.setView([25, 0], 2)
        this._watchSize()

        try {
            let response = await fetch(this.urlValue, { headers: { Accept: "application/json" }, credentials: "same-origin" })
            if (!response.ok) throw new Error("map data failed")
            let payload = await response.json()
            this._renderCities(payload.cities || [])
            this._writeFooter(payload)
        } catch (_error) {
            // Leave the empty world map; tiles already attribute OSM.
        }
    }

    disconnect() {
        this._resizes?.disconnect()
        this._resizes = null
        if (this._map) {
            this._map.remove()
            this._map = null
        }
    }

    // Leaflet caches the container's size, so anything that resizes it leaves
    // the map drawing a grid for a box it no longer fills - grey bands along the
    // edges with no tiles in them. Its own listener is on window.resize, which
    // covers the browser being dragged and nothing else: this page's canvas also
    // changes height when the coverage line appears under it and when the scope
    // toggle appears above it, and width when the assistant panel opens.
    _watchSize() {
        if (typeof ResizeObserver !== "function") return

        let pending = false
        this._resizes = new ResizeObserver(() => {
            if (pending || !this._map) return

            pending = true
            requestAnimationFrame(() => {
                pending = false
                if (!this._map) return

                this._map.invalidateSize()
            })
        })
        this._resizes.observe(this.mapTarget)
    }

    closePanel() {
        this.panelTarget.hidden = true
    }

    // NOT ATTEMPTED: forcing the map to always cover the canvas, by refusing to
    // zoom out past the level where the world fills it. On a tall viewport a
    // world map does letterbox - grey above and below a world that has run out -
    // and a minZoom floor does remove the bands. It also stops fitBounds from
    // framing the pins: on a real address book it cut the Americas off the
    // screen, hiding the 62 contacts in San Francisco behind a pan. Showing
    // everybody beats filling the box.

    // Below this, a face is 34 pixels of nobody: the whole world is on screen,
    // the bubbles are what you read, and fetching a photo for each of them is
    // both a slow first paint and - on a real address book with ~1,500
    // one-person bubbles - enough requests to trip the image rate limiter.
    // Around country level a face starts being a face.
    static FACE_ZOOM = 5

    _renderCities(cities) {
        if (cities.length === 0) return

        this._faces = []
        let bounds = []
        cities.forEach((city) => {
            if (city.lat == null || city.lon == null) return
            bounds.push([city.lat, city.lon])
            let size = Math.min(56, 28 + Math.round(Math.sqrt(city.count) * 6))
            let icon = L.divIcon({
                className: "network-map-bubble-wrap",
                html: this._bubble(city, size),
                iconSize: [size, size],
                iconAnchor: [size / 2, size / 2]
            })
            L.marker([city.lat, city.lon], { icon: icon, title: city.label })
                .addTo(this._map)
                .on("click", () => this._openPanel(city))
        })
        if (bounds.length > 0) this._map.fitBounds(bounds, { padding: [40, 40], maxZoom: 10 })

        this._showFaces()
        this._map.on("zoomend moveend", () => this._showFaces())
    }

    // Load the faces that are worth loading: zoomed in enough to read, and on
    // screen. Each one is loaded once and then left alone - zooming back out
    // does not throw the photo away, because the request has already happened
    // and a second one on the way back in would be the waste this avoids.
    _showFaces() {
        if (!this._map || !this._faces?.length) return
        if (this._map.getZoom() < this.constructor.FACE_ZOOM) return

        this._faces.forEach((photo) => {
            if (photo.src || !photo.dataset.src || !photo.isConnected) return

            // The bubble's own position, which Leaflet has already placed.
            let marker = photo.closest(".network-map-bubble-wrap")
            if (!marker) return

            let box = marker.getBoundingClientRect()
            let canvas = this.mapTarget.getBoundingClientRect()
            let onScreen = box.right > canvas.left && box.left < canvas.right &&
                box.bottom > canvas.top && box.top < canvas.bottom
            if (!onScreen) return

            photo.src = photo.dataset.src
        })
    }

    // A bubble is a COUNT when it stands for several people, and a PERSON when
    // it stands for one - so when it stands for one it shows their face. A map
    // of a thousand circles each saying "1" tells you nothing you did not
    // already get from the dot being there; a face tells you who, which is the
    // question you opened a map of your network to ask.
    //
    // Built as an element rather than an HTML string because a broken photo has
    // to fall back to initials, and an inline onerror= is an inline script that
    // this app's CSP blocks.
    _bubble(city, size) {
        let bubble = document.createElement("span")
        bubble.className = "network-map-bubble"
        // A COUNTRY OR A REGION is not a place anybody lives. Its coordinates
        // are the middle of a shape - "United States" is a dot in Kansas - so
        // it is drawn hollow, and never with a face: a photograph on it would
        // imply we know where that person is.
        if (city.coarse) bubble.classList.add("network-map-bubble--coarse")
        bubble.style.width = `${size}px`
        bubble.style.height = `${size}px`

        let only = city.count === 1 ? (city.contacts || [])[0] : null
        if (!only) {
            bubble.textContent = String(city.count)
            return bubble
        }

        // Their initial, not "1" - true whether or not a photo loads.
        let initial = document.createElement("span")
        initial.className = "network-map-bubble-initial"
        initial.textContent = only.initial || "?"
        bubble.appendChild(initial)
        bubble.title = only.name || ""
        if (city.coarse || !only.avatar_url) return bubble

        let photo = document.createElement("img")
        photo.className = "network-map-bubble-photo"
        photo.alt = ""
        // LAZY, deliberately. A real address book draws ~1,500 single-contact
        // bubbles, and fetching every face the moment the map opens is both a
        // slow first paint and enough requests to trip the image rate limiter.
        // Leaflet keeps off-screen markers in the DOM, so the browser's own
        // viewport check is exactly the throttle wanted here: faces arrive as
        // you pan to them.
        photo.loading = "lazy"
        photo.decoding = "async"
        photo.referrerPolicy = "no-referrer"
        photo.addEventListener("error", () => photo.remove(), { once: true })
        // The src is NOT set here - see _showFaces. It is set when the map is
        // zoomed in far enough for a 34px face to mean anything, and only for
        // the bubbles actually on screen.
        photo.dataset.src = only.avatar_url
        bubble.appendChild(photo)
        this._faces.push(photo)
        return bubble
    }

    // WHAT IS NOT ON THE MAP. An address book of 1,652 draws 492 dots, and
    // without a line saying so that reads as a broken map rather than as an
    // address book whose cities are mostly unrecorded. The same line the family
    // tree's map learned to draw.
    _writeFooter(payload) {
        if (!this.hasFooterTarget) return

        let coverage = payload.coverage
        if (!coverage) {
            this.footerTarget.textContent = ""
            return
        }

        let mapped = (payload.cities || []).reduce((sum, city) => sum + Number(city.count), 0)
        let places = (payload.cities || []).length
        let parts = [`${mapped} ${mapped === 1 ? "contact" : "contacts"} in ${places} ${places === 1 ? "place" : "places"}`]
        if (coverage.unplaced > 0) parts.push(`${coverage.unplaced} couldn't be located`)
        let without = Number(coverage.total) - Number(coverage.located)
        if (without > 0) {
            parts.push(`${without} ${without === 1 ? "contact has" : "contacts have"} no location recorded`)
        }
        this.footerTarget.textContent = parts.join(" · ")
    }

    _openPanel(city) {
        this.panelTitleTarget.textContent = `${city.coarse ? `${city.label} (region)` : city.label} · ${city.count}`
        this.panelListTarget.replaceChildren()
        city.contacts.forEach((contact) => {
            let row = document.createElement("a")
            row.className = "network-map-contact"
            row.href = contact.url
            let avatar
            if (contact.avatar_url) {
                avatar = document.createElement("img")
                avatar.className = "contact-avatar network-map-contact-avatar"
                avatar.src = contact.avatar_url
                avatar.alt = ""
                avatar.referrerPolicy = "no-referrer"
            } else {
                avatar = document.createElement("span")
                avatar.className = "contact-avatar network-map-contact-avatar"
                avatar.textContent = contact.initial || "?"
            }
            let body = document.createElement("span")
            body.className = "network-map-contact-body"
            let name = document.createElement("span")
            name.className = "network-map-contact-name"
            name.textContent = contact.name
            body.appendChild(name)
            // Title and company, where the row has them. On the org-wide map a
            // city can hold several people with the same name from different
            // teammates' address books, and the name alone does not say which
            // is which.
            if (contact.meta) {
                let meta = document.createElement("span")
                meta.className = "network-map-contact-meta"
                meta.textContent = contact.meta
                body.appendChild(meta)
            }
            row.append(avatar, body)
            this.panelListTarget.appendChild(row)
        })
        this.panelTarget.hidden = false
    }
}
